import React, { useState } from 'react';
import { IPDASetting } from '../../models/IPDASetting.model';
import { Form, Formik } from 'formik';
import yup from '../../common/validation';
import { Button, Modal } from 'react-bootstrap';
import { Spinner2 } from '../shared/spinner2';
import TextField from '../shared/formikFields/TextField';
import { Form as BsForm, Row, Col } from 'react-bootstrap';
import ToggleButton from '../shared/customInputTypes/ToggleButton';
import { debounceEventHandler } from '../shared/submitFormUtility';
import { PdaSettingsService } from '../../services/pdaSettingsService';
import { Toastr } from '../../utils/Toastr';
import { locale } from '../../common/localization/localizationService';

export interface IPDASettingModalProps {
  customerId: number;
  show: boolean;
  pdaSetting: IPDASetting;
  onHide: (rerender: boolean) => void;
  existingParameters: Array<string>;
}

interface IPDASettingFormValues extends Omit<
  IPDASetting,
  'minValue' | 'maxValue'
> {
  minValue: number | '';
  maxValue: number | '';
}

export default function PdaSettingModal(
  props: IPDASettingModalProps,
): JSX.Element {
  const newPdaSetting = () => {
    const pdaSetting: IPDASetting = {
      customerId: props.customerId,
      parameter: '',
      required: false,
      value_NO: null,
      value_ENG: null,
      minValue: null,
      maxValue: null,
    };
    return pdaSetting;
  };
  const isNew = !props.pdaSetting;
  const pdaSetting = isNew ? newPdaSetting() : props.pdaSetting;

  const { show, onHide } = props;

  const [isLoading, setLoading] = useState(false);

  const getSchema = () =>
    yup.object({
      parameter: isNew
        ? yup
            .string()
            .required()
            .test(
              'is-unique',
              locale.pdaSettings._parameterAlreadyExits,
              (routeId: string) =>
                !props.existingParameters ||
                !routeId ||
                !props.existingParameters.includes(routeId.trim()),
            )
            .label(locale.pdaSettings._parameter)
        : yup.string().required().label(locale.pdaSettings._parameter),

      minValue: yup
        .number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v)),

      maxValue: yup
        .number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v)),
    });

  const onSubmit = async (values: IPDASettingFormValues) => {
    const payload: IPDASetting = {
      ...values,
      minValue: values.minValue === '' ? null : Number(values.minValue),
      maxValue: values.maxValue === '' ? null : Number(values.maxValue),
    };

    if (isNew) {
      try {
        setLoading(true);
        await PdaSettingsService.CreateNewPdaSetting(payload);
        Toastr.success(locale.pdaSettings._createSuccess);
      } catch (err) {
        Toastr.error(err);
      } finally {
        setLoading(false);
        props.onHide(true);
      }
    } else {
      try {
        setLoading(true);
        await PdaSettingsService.UpdatePDASetting(payload);
        Toastr.success(locale.pdaSettings._updateSuccess);
      } catch (err) {
        Toastr.error(err);
      } finally {
        setLoading(false);
        props.onHide(true);
      }
    }
  };

  const initialValues: IPDASettingFormValues = {
    ...pdaSetting,
    minValue: pdaSetting.minValue ?? '',
    maxValue: pdaSetting.maxValue ?? '',
  };

  return (
    <>
      <Formik<IPDASettingFormValues>
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={getSchema()}
      >
        {({
          handleSubmit,
          values,
          isSubmitting,
          isValid,
          dirty,
          setFieldValue,
        }) => (
          <Modal show={show} onHide={() => onHide(false)}>
            <Spinner2 show={isLoading} />
            <Modal.Header closeButton>
              <Modal.Title>
                {isNew
                  ? locale.pdaSettings._newPDASetting
                  : locale.pdaSettings._udatePDASetting}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form noValidate onSubmit={handleSubmit}>
                <TextField
                  label={locale.pdaSettings._parameter}
                  name="parameter"
                  placeholder={locale.pdaSettings._parameter}
                  disabled={!isNew}
                />
                <TextField
                  label={locale.pdaSettings._valueNO}
                  name="value_NO"
                  placeholder={locale.pdaSettings._valueNO}
                />
                <TextField
                  label={locale.pdaSettings._valueENG}
                  name="value_ENG"
                  placeholder={locale.pdaSettings._valueENG}
                />
                <TextField
                  label={locale.pdaSettings._minValue}
                  name="minValue"
                  placeholder={locale.pdaSettings._minValue}
                />
                <TextField
                  label={locale.pdaSettings._maxValue}
                  name="maxValue"
                  placeholder={locale.pdaSettings._maxValue}
                />
                <BsForm.Group
                  as={Row}
                  className="checkbox-row mb-3 route-active-input"
                >
                  <BsForm.Label column sm={6}>
                    {locale.pdaSettings._required}
                  </BsForm.Label>
                  <Col sm={6} className="checkbox-wrapper">
                    <ToggleButton
                      onToggle={(e) => {
                        setFieldValue('required', e.currentTarget.checked);
                      }}
                      value={values.required}
                    />
                  </Col>
                </BsForm.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => onHide(false)}>
                {locale.pdaSettings._cancel}
              </Button>
              <Button
                type="submit"
                variant="primary"
                onClick={debounceEventHandler(handleSubmit, 250)}
                disabled={
                  !values.parameter?.trim() ||
                  isSubmitting ||
                  !isValid ||
                  (!isNew && !dirty)
                }
              >
                {locale.pdaSettings._save}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </Formik>
    </>
  );
}
